import { styled } from "@linaria/react"
import { useNavigate } from "@tanstack/react-router"
import { useState } from "react"

import { Centered } from "@/components/Centered/Centered.ts"
import { Dialog } from "@/components/Dialog/Dialog.tsx"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { GradientBlob } from "@/components/GradientBlob.ts"
import { MarkdownWrapper } from "@/components/Markdown/MarkdownWrapper.tsx"
import { useCloseLocation, useOrgHomeLocation } from "@/hooks/useMatterLocation.ts"
import { CloseLight } from "@/icons/Icons.ts"
import { pageTitle } from "@/lib/displayUtils.ts"
import { startViewTransition } from "@/lib/dom.ts"
import { InviteTeammatesDialog } from "@/routes/auth/InviteTeammatesDialog.tsx"
import { Page } from "@/routes/auth/Page.tsx"
import { HomeStyles } from "@/routes/home/HomeStyles.ts"
import { NewProjectForm } from "@/routes/projects/NewProjectForm.tsx"
import { useOrganizationPermissions } from "@/routes/projects/projectHooks.ts"
import { Li, Ol } from "@/styles/Markdown.ts"


export const NewProjectScene = () => {
  const navigate = useNavigate()
  const closeLocation = useCloseLocation(useOrgHomeLocation())

  const leave = () => startViewTransition(() => navigate(closeLocation))

  const [howToDialogOpen, setHowToDialogOpen] = useState(false)
  const [inviteTeammatesDialogOpen, setInviteTeammatesDialogOpen] = useState(false)

  const permissions = useOrganizationPermissions()

  return (
    <Page>
      <title>{pageTitle("New project")}</title>

      <HomeStyles.PageCloseButton kind="icon" onClick={leave}>
        <CloseLight />
      </HomeStyles.PageCloseButton>

      <FormCentered vertical={false}>
        <FormTitle data-testid="new-project-title">
          Add your project
        </FormTitle>

        <LimitedWidthForm>
          <Vertical gap="24px">
            <FormSubtitle>
              To get started, connect an existing repository.
              Matter will mirror your codebase and set it up to bring your ideas to life.
            </FormSubtitle>

            <NewProjectForm />

            <Vertical alignItems="center">
              <HelpLinks>
                <Dialog
                  open={howToDialogOpen}
                  onOpenChange={setHowToDialogOpen}
                  title="Where to find the link?"
                  trigger={(
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        setHowToDialogOpen(true)
                      }}
                      data-testid="help-popup"
                    >
                      Where to find the link
                    </a>
                  )}
                >
                  <Vertical gap="16px" alignItems="flex-start">
                    <HowToMarkdown text={howToText}></HowToMarkdown>

                    {permissions.canInviteUsers && (
                      <>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault()
                            setInviteTeammatesDialogOpen(true)
                          }}
                          data-testid="invite-developer"
                        >
                          Invite developer
                        </a>

                        <InviteTeammatesDialog
                          dialogOpen={inviteTeammatesDialogOpen}
                          setDialogOpen={setInviteTeammatesDialogOpen}
                        />
                      </>
                    )}
                  </Vertical>
                </Dialog>

                {permissions.canInviteUsers && (
                  <>
                    <a href="#" onClick={(e) => {
                      e.preventDefault()
                      setInviteTeammatesDialogOpen(true)
                    }}>Invite developer</a>

                    <InviteTeammatesDialog
                      dialogOpen={inviteTeammatesDialogOpen}
                      setDialogOpen={setInviteTeammatesDialogOpen}
                    />
                  </>
                )}
              </HelpLinks>
            </Vertical>
          </Vertical>
        </LimitedWidthForm>
      </FormCentered>

      <GradientBlob />
    </Page>
  )
}

const howToText = `
1. **Check where your repository is hosted**

   Most teams use platforms like GitHub, GitLab, or Bitbucket.

   * Ask your teammates which one your project uses.
   * Go to the hosting site, open the repository page, and copy the repository URL (usually found behind a "Clone" or "Code" button).

2. **Ask a developer on your team**

   A teammate can quickly send you the repository link and make sure you have access. You might need to be invited if it's a private project.

3. **Look in project documentation or chat history**

   Sometimes the repo link is already shared in a onboarding doc, README file, or a pinned message in Slack or Discord.

**Still can't find it?**

Invite a developer to join you and ask them to set up the repository connection for you. It only takes a minute.
`

const FormCentered = styled(Centered)`
  margin-top: 48px;
  min-height: auto;
`

const FormTitle = styled(HomeStyles.PageSubtitle)`
  view-transition-name: new-project-h2;
`

const FormSubtitle = styled.div`
  text-align: center;
`

const AnimatedContent = styled.div`
  width: 100%;

  view-transition-name: new-project-content;
`

const LimitedWidthForm = styled(AnimatedContent)`
  max-width: 560px;
`

const HowToMarkdown = styled(MarkdownWrapper)`
  & > .${Ol} > .${Li}:not(:last-child) {
    margin-bottom: 16px;
  }
`

const HelpLinks = styled(Horizontal)`
  gap: 24px;
  white-space: nowrap;
`
